import './ShareFormV2Modal.css'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import {
  Align,
  Button,
  Checkbox,
  Input,
  InputGroup,
  InputGroupAddon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Switch,
  Tab,
  Tabs,
  Textarea,
  Tooltip,
} from '@sm/wds-react'
import {
  IconCheck,
  IconEmail,
  IconEye,
  IconLink,
  IconLock,
  IconLogoFacebook,
  IconLogoLinkedIn,
  IconLogoTwitter,
  IconPencilPaper,
  IconWarning,
  IconX
} from '@sm/wds-icons'
import UpgradePopout from '../../../formBuilderWeb/components/permissions/UpgradePopout/UpgradePopout'
import { getPlainTextFromMarkup } from '../../models/styling'
import { API_ERRORS } from '../../constants/errors'
import { ENTER, ESC, ESCAPE } from '../../constants/keyboardEvents'
import { MODAL_MIXPANEL_EVENT_PROPERTIES } from '../../constants/mixpanelEventProperties'
import { USER } from '../../constants/propTypes/user'


const ShareFormV2Modal = props => {
  const {
    apiError,
    closeModal,
    deleteAlias,
    fetchAlias,
    flagConfig,
    formAlias,
    formId,
    formTitle,
    hiddenFields,
    isAliasLoading,
    isHiddenFieldsEnabled,
    language,
    setApiError,
    setToast,
    renderTo,
    translate,
    updateAlias,
    user
  } = props

  const [ isCustomUrl, setIsCustomUrl ] = useState(isHiddenFieldsEnabled && !!formAlias)
  const [ isEditingUrl, setIsEditingUrl ] = useState(false)
  const [ isLinkMode, setIsLinkMode ] = useState(true)

  const [ includeHiddenFields, setIncludeHiddenFields ] = useState(false)
  const { enabled: hiddenFieldEnabled = false, fields = []} = hiddenFields || {}
  const linkInputRef = useRef(null)

  const getHiddenFieldsEncoded = fields => {
    return '?' + fields.map(field => `${encodeURIComponent(field?.title)}=${encodeURIComponent(field?.defaultValue)}`).join('&') + '\u202a'
  }
  const hiddenFieldsValue = useMemo(() => includeHiddenFields ? getHiddenFieldsEncoded(fields) : '', [ includeHiddenFields, fields ])

  const conflictingFormName = apiError?.alias?.errorProps?.formName || translate('another_form')
  const domain = flagConfig?.domain
  const protocol = flagConfig?.protocol || 'https'
  const subdomain = user?.subdomain
  const userEmail = user?.email

  //replace spaces in form title with dashes then remove &?/\ characters
  const regexFormTitle = formTitle.replace(/[\s]+/g, '-')
  const sanitizedFormTitle = getPlainTextFromMarkup(regexFormTitle?.replace(/[&?\\/]+/g, '')?.toLowerCase())

  const [ lastUsedAlias, setLastUsedAlias ] = useState(formAlias)
  const defaultInputValue = isCustomUrl ? (formAlias || lastUsedAlias || sanitizedFormTitle) : formId
  const [ inputValue, setInputValue ] = useState(defaultInputValue)
  const respondentUrlPrefix = `${protocol}://${domain}/r/${subdomain}/`
  const respondentUrl = `${respondentUrlPrefix}${isCustomUrl ? formAlias : `forms/${defaultInputValue}`}${hiddenFieldEnabled ? hiddenFieldsValue : ''}`

  useEffect(() => {
    // only fetch alias if not found in the aliases map
    if (formAlias === undefined) {
      fetchAlias(formId)
    }
  }, []) //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    !isEditingUrl && setInputValue(formAlias)
  }, [ isEditingUrl, formAlias ])

  useEffect(() => {
    setInputValue(defaultInputValue)
  }, [ defaultInputValue ])

  useEffect(() => {
    isHiddenFieldsEnabled && formAlias && setIsCustomUrl(true)
  }, [ formAlias, isHiddenFieldsEnabled ])

  const handleClose = () => {
    setApiError({ alias: null })
    closeModal()
  }

  const handleSwitch = () => {
    setIsCustomUrl(!isCustomUrl)
    setApiError({ alias: null })
    if (isCustomUrl) {
      setIsEditingUrl(false)
      formAlias && deleteAlias(formId)

      setToast({
        autoClose: false,
        type: 'warning',
        title: 'Custom_link_deleted_title',
        body: 'Custom_link_deleted_description',
        dismissible: true,
      })
    } else {
      setIsEditingUrl(true)
    }
  }

  const handleCopy = element => {
    element.select()
    const isSupported = document.execCommand('copy')

    if (isSupported) {
      setToast({
        type: 'success',
        title: 'copied_to_clipboard_success_toast',
        dismissible: false,
      })
    }
    else {
      setToast({
        type: 'error',
        title: 'copied_to_clipboard_fail_toast',
        dismissible: false,
      })
    }
  }

  const handleFormAlias = () => {
    const alias = inputValue || defaultInputValue

    updateAlias(formId, { alias, language })
    setLastUsedAlias(alias)

    // Only show warning toast if changing an existing alias
    if (lastUsedAlias && alias) {
      setToast({
        autoClose: false,
        type: 'warning',
        title: 'Custom_link_updated_title',
        body: 'Custom_link_updated_description',
        dismissible: true,
      })
    }

    setIsEditingUrl(false)
  }

  const handleCancel = () => {
    setInputValue(defaultInputValue)
    setIsEditingUrl(false)
  }

  const handleOnChange = (e) => {
    setIsEditingUrl(true)
    setApiError({ alias: null })
    setInputValue(e.target.value)
  }

  const handleKeyPress = e => {
    switch (e.key){
      case '/':
      case ' ':
      case '?':
      case '&':
        e.preventDefault()
        break
      case ENTER:
        return handleFormAlias(e)
      case ESC:
      case ESCAPE:
        return handleCancel(e)
      default:
        break
    }
  }

  const handleFacebookLink = () => {
    const facebookAppId = process.env.REACT_APP_FACEBOOK_ID
    const link = `https://www.facebook.com/dialog/share?href=https%3A%2F%2F${subdomain}.${domain}%2Fforms%2F${formId}%2F&app_id=${facebookAppId}&quote=Check+out+my+form`
    window.open(link, '_blank', 'location=yes,height=570,width=520,scrollbars=yes,status=yes')
  }

  const handleTwitterLink = () => {
    const link = `https://twitter.com/intent/tweet?via=wufoo&text=Check+out+my+form%21&url=https%3A%2F%2F${subdomain}.${domain}%2Fforms%2F${formId}%2F`
    window.open(link, '_blank', 'location=yes,height=570,width=520,scrollbars=yes,status=yes')
  }

  const handleLinkedInLink = () => {
    const link = `https://www.linkedin.com/shareArticle?title=Check+out+my+form%21&summary=${sanitizedFormTitle}&url=https%3A%2F%2F${subdomain}.${domain}%2Fforms%2${formId}%2F`
    window.open(link, '_blank', 'location=yes,height=570,width=520,scrollbars=yes,status=yes')
  }

  const handleEmailLink = () => {
    const link = `mailto:${userEmail}?body=Hey%20friend%2C%20check%20out%20this%20new%20form%20I%20just%20made%20with%20Wufoo%21%0A%0Ahttps%3A%2F%2F${subdomain}.${domain}%2Fforms%2F${formId}%2F&subject=Check%20out%20my%20form%21`
    window.open(link, '_blank', 'location=yes,height=570,width=520,scrollbars=yes,status=yes')
  }

  const renderIncludeHiddenFieldsCheckbox = (isHiddenFieldsEnabled) => {
    return (
      <div className='share-modal-section checkbox' >
        <Checkbox
          disabled={(isEditingUrl || apiError.alias) || (!hiddenFieldEnabled || fields?.length === 0)}
          checked={includeHiddenFields}
          label={translate('Include_hidden_field_parameters')}
          onChange={() => setIncludeHiddenFields(!includeHiddenFields)}
        />
        {!isHiddenFieldsEnabled && renderUpgradePopout('hidden-fields', 'Upgrade_to_include_hidden_fields')}
      </div>
    )
  }

  const renderEmbedMode = isHiddenFieldsEnabled => {
    const value = `<script wufoo-profile-name="${subdomain}" wufoo-form-id="${formId}" src="${protocol}://${domain}/embed.js${hiddenFieldsValue}"></script>`
    return (
      <>
        <div className='embed-form-label'>{translate('paste_this_into_your_website')}</div>
        {renderIncludeHiddenFieldsCheckbox(isHiddenFieldsEnabled)}
        <InputGroup>
          <Textarea
            className='embed-code-textarea'
            readOnly
            value={value}
            onFocus={e => e.target.select()}
          />
          <InputGroupAddon>
            <Button
              color='secondary'
              onClick={() => handleCopy(document.querySelector('.embed-code-textarea'))}
              size='md'
            >
              {translate('Copy')}
            </Button>
          </InputGroupAddon>
        </InputGroup>
      </>
    )
  }

  const renderLinkMode = isHiddenFieldsEnabled => {
    return (
      <>
        <div className='share-modal-section'>
          <Switch
            className='default-custom-switch'
            checked={isCustomUrl}
            disabled={!isHiddenFieldsEnabled}
            onChange={handleSwitch}
          />
          <span className='custom-text'>{translate('Custom')}</span>
          {!isHiddenFieldsEnabled && renderUpgradePopout('custom-url', 'Upgrade_to_edit_share_url')}
        </div>
        {renderIncludeHiddenFieldsCheckbox(isHiddenFieldsEnabled)}
        <InputGroup
          stretched
          color={apiError.alias && 'warning'}
          disabled={isAliasLoading}
        >
          <InputGroupAddon>
            <div className='link-icon'>
              <IconLink />
            </div>
          </InputGroupAddon>

          {isEditingUrl || apiError.alias ? (
            <>
              <div className='non-editable-input'>
                {respondentUrlPrefix}
              </div>
              <Input
                autoFocus
                className='editable-input'
                inputRef={linkInputRef}
                maxLength={'100'}
                onChange={handleOnChange}
                onClick={() => linkInputRef.current.select()}
                onKeyPress={handleKeyPress}
                value={inputValue || ''}
              />
              <InputGroupAddon>
                <Button
                  className='edit-button-cancel'
                  color='secondary'
                  disabled={inputValue?.length === 0 || defaultInputValue !== formAlias}
                  onClick={() => setIsEditingUrl(false)}
                  size='sm'
                  variant='ghost'
                >
                  <IconX />
                </Button>
                <Button
                  className='edit-button-ok'
                  disabled={inputValue?.length === 0}
                  onClick={handleFormAlias}
                  size='sm'
                >
                  <IconCheck />
                </Button>
              </InputGroupAddon>
            </>

          ) : (

            <>
              <Input
                className={classnames(
                  { 'scrolled' : includeHiddenFields }
                )}
                inputRef={linkInputRef}
                onClick={() => linkInputRef.current.select()}
                readOnly
                value={!isAliasLoading ? (
                  respondentUrl
                ) : (
                  `${respondentUrlPrefix}...`
                )}
              />
              <InputGroupAddon>
                {isCustomUrl && (
                  <Button
                    className='edit-button'
                    color='muted'
                    onClick={() => setIsEditingUrl(true)}
                    size='md'
                  >
                    <IconPencilPaper />
                  </Button>
                )}
                <Tooltip
                  placement='bottom'
                  renderTo={renderTo}
                  text={translate('open_form_in_new_tab')}
                >
                  <Button
                    className='view-button'
                    color='muted'
                    onClick={() => window.open(respondentUrl, '_blank')}
                    size='md'
                  >
                    <IconEye />
                  </Button>
                </Tooltip>
                <Button
                  color='secondary'
                  onClick={() => handleCopy(linkInputRef.current)}
                  size='md'
                >
                  {translate('Copy')}
                </Button>
              </InputGroupAddon>
            </>
          )}
        </InputGroup>
        {apiError.alias && (
          <div className='error-message'>
            <IconWarning /> { apiError.alias?.errorName === API_ERRORS.ALIAS.ALIAS_IN_USE ? translate('form_alias_in_use', { conflictingFormName }) : translate('form_alias_error') }
          </div>
        )}
      </>
    )
  }

  const renderUpgradePopout = (key, errorMessage) => {
    return (
      <UpgradePopout
        body={translate(errorMessage)}
        placement='right'
        renderTo={renderTo}
        shouldSendTrackingEvent={true}
        trackingEventProps={MODAL_MIXPANEL_EVENT_PROPERTIES[key]}
        translate={translate}
        trigger={<IconLock />}
      />
    )
  }

  return (
    <Modal
      className='share-form-modal'
      onClose={handleClose}
      size='md'
      show={true}
    >
      <ModalHeader
        header={translate('Share_form')}
      />

      <ModalBody>
        <Tabs
          onChange={() => setIsLinkMode(!isLinkMode)}
        >
          <Tab
            selected={isLinkMode}
            title={translate('Link')}
          />
          <Tab
            selected={!isLinkMode}
            title={translate('Embed')}
          />
        </Tabs>
        <div
          className={classnames(
            'share-modal-container',
            `${isLinkMode ? 'link' : 'embed' }`
          )}
        >
          {isLinkMode ? renderLinkMode(isHiddenFieldsEnabled) : renderEmbedMode(isHiddenFieldsEnabled)}
          <div className='share-buttons'>
            <h3>{translate('More_options')}</h3>
            <div className='share-button-group'>
              <Button onClick={handleFacebookLink}><IconLogoFacebook /></Button>
              <Button onClick={handleTwitterLink}><IconLogoTwitter /></Button>
              <Button onClick={handleLinkedInLink}><IconLogoLinkedIn /></Button>
              <Button onClick={handleEmailLink}><IconEmail /></Button>
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Align placement='right'>
          <Button
            className='cancel-button'
            disabled={isEditingUrl}
            onClick={handleClose}
          >
            {translate('Done')}
          </Button>
        </Align>
      </ModalFooter>

    </Modal>
  )
}

ShareFormV2Modal.defaultProps = {
  renderTo: null,
}

ShareFormV2Modal.propTypes = {
  apiError: PropTypes.shape({
    alias: PropTypes.shape({
      errorName: PropTypes.string,
      errorProps: PropTypes.object
    })
  }),
  closeModal: PropTypes.func.isRequired,
  deleteAlias: PropTypes.func.isRequired,
  fetchAlias: PropTypes.func.isRequired,
  flagConfig: PropTypes.shape({
    domain: PropTypes.string,
    protocol: PropTypes.string,
  }),
  formAlias: PropTypes.string,
  formId: PropTypes.string.isRequired,
  formTitle: PropTypes.string,
  hiddenFields: PropTypes.shape({
    enabled: PropTypes.bool,
    fields: PropTypes.array,
  }),
  isAliasLoading: PropTypes.bool.isRequired,
  isHiddenFieldsEnabled: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  renderTo: PropTypes.instanceOf(Element),
  setApiError: PropTypes.func.isRequired,
  setToast: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  updateAlias: PropTypes.func.isRequired,
  user: PropTypes.shape(USER).isRequired,
}

export default ShareFormV2Modal
